/**
 * References:
 * - https://github.com/adamsilver/nostyle/blob/master/src/components/autocomplete/Autocomplete.js
 * - https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
 */

/**
 * A custom element for enhancing select boxes into comboboxes with custom option markup.
 * This should not be directly consumed -- it should be extended with getOptionAttributes and getOptions set on it. (defaults to getOptionHtml when not provided)
 */
export default class BaseCustomSelect extends HTMLElement {
  constructor() {
    super();

    this.comboboxArrow = `<span class="arrow"><svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" height="1em" width="1em"><path d="M201.4 342.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 274.7 86.6 137.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"></path></svg></span>`;

    this.render = () => {
      if (!this.getOptionHtml) {
        // biome-ignore lint/suspicious/noConsole: debugging
        console.warn('getOptionHtml is not provided; will act as a normal select input', this);
        return;
      }

      this.select = this.querySelector('select');

      if (!this.select) {
        // biome-ignore lint/suspicious/noConsole: debugging
        console.warn('select is not provided in light DOM; will not enhance anything', this);
        return;
      }

      this.createCombobox();
      this.createListbox();
      this.hideSelectBox();
    };
  }

  connectedCallback() {
    self.addEventListener(`${this.localName}:manual-set-option`, this.handleManualSetOption);
    this.render();
  }

  disconnectedCallback() {
    self.removeEventListener(`${this.localName}:manual-set-option`, this.handleManualSetOption);
    document.removeEventListener('click', this.lightDismissHandler);
  }

  /**
   * Emit a custom event
   * @param {string} type - The event type
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (type, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(`${this.localName}:${type}`, {
      bubbles: true,
      cancelable: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  optionClassName = null;

  getInitialValue = () => {
    return this.querySelector('select').value;
  };

  createCombobox = () => {
    this.querySelector('label').id = `${this.select.id}-label`;

    const combobox = document.createElement('button');
    combobox.setAttribute('type', 'button');
    combobox.setAttribute('role', 'combobox');
    combobox.setAttribute('aria-expanded', false);
    combobox.setAttribute('aria-controls', `${this.select.id}-listbox`);
    combobox.setAttribute('aria-labelledby', `${this.select.id}-label`);

    const initialValue = this.getInitialValue();

    this.select.value = initialValue;

    combobox.innerHTML = this.getComboboxHtml
      ? this.getComboboxHtml(initialValue)
      : this.getOptionHtml(initialValue);
    combobox.innerHTML += this.comboboxArrow;
    combobox.addEventListener('click', this.comboboxClickHandler);
    combobox.addEventListener('keydown', this.comboboxKeydownHandler);
    combobox.addEventListener('keyup', this.comboboxKeyupHandler);
    combobox.addEventListener('focus', this.comboboxFocusHandler);

    this.appendChild(combobox);
  };

  createListbox = () => {
    const listbox = document.createElement('ul');
    listbox.setAttribute('role', 'listbox');
    listbox.id = `${this.select.id}-listbox`;
    listbox.setAttribute('aria-labelledby', `${this.select.id}-label`);
    listbox.style.display = 'none';
    listbox.addEventListener('keydown', this.menuKeyDownHandler);
    listbox.addEventListener('click', this.menuClickHandler);

    this.appendChild(listbox);

    const selectOptions = this.select.querySelectorAll('option');

    for (const selectOption of selectOptions) {
      const option = document.createElement('li');

      if (this.optionClassName) {
        option.setAttribute('className', this.optionClassName);
      }

      option.setAttribute('role', 'option');
      option.setAttribute('data-option-value', selectOption.value);
      option.id = `${this.select.id}-${selectOption.value.toLowerCase()}-option`;
      option.tabIndex = -1;
      option.setAttribute('aria-selected', this.select.value === selectOption.value ? 'true' : 'false');
      option.innerHTML = this.getOptionHtml(selectOption.value);
      option.addEventListener('mouseenter', this.optionMouseEnterHandler);

      listbox.appendChild(option);
    }

    document.addEventListener('click', this.lightDismissHandler);
  };

  lightDismissHandler = (event) => {
    if (!event.composedPath().includes(this)) {
      this.hideListbox();
      this.removeComboboxFocus();
    }
  };

  hideSelectBox = () => {
    this.select.setAttribute('aria-hidden', 'true');
    this.select.setAttribute('tabindex', '-1');
    this.select.classList.add('visually-hidden');
    this.select.id = '';
  };

  comboboxFocusHandler = () => {
    this.querySelector('[role="combobox"]').setAttribute('data-focused', 'true');
  };

  removeComboboxFocus = () => {
    this.querySelector('[role="combobox"]').removeAttribute('data-focused');
  };

  comboboxClickHandler = () => {
    const isComboboxExpanded = this.querySelector('[role="combobox"]').getAttribute('aria-expanded') === 'true';

    if (isComboboxExpanded) {
      this.hideListbox();
    } else {
      this.showListbox();
      const initialOption = this.getActiveOption() || this.getFirstOption();
      this.highlightOption(initialOption);
    }
  };

  comboboxKeydownHandler = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.showListbox();
        this.highlightOption(this.getActiveOption());
        break;
      case 'Tab':
        this.hideListbox();
        this.removeComboboxFocus();
        break;
      default:
        break;
    }
  };

  comboboxKeyupHandler = (e) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault(); // prevent bubbling if open in modal
        this.hideListbox();
        break;
      default:
        break;
    }
  };

  showListbox = () => {
    this.querySelector('[role="combobox"]').setAttribute('aria-expanded', 'true');
    this.querySelector('[role="listbox"]').style.display = 'block';
  };

  hideListbox = () => {
    this.querySelector('[role="combobox"]').setAttribute('aria-expanded', 'false');
    this.querySelector('[role="listbox"]').style.display = 'none';
  };

  optionMouseEnterHandler = (e) => {
    this.highlightOption(e.target);
  };

  menuClickHandler = (e) => {
    this.selectOption(e.target);
  };

  menuKeyDownHandler = (e) => {
    switch (e.key) {
      case 'ArrowUp':
        // want to highlight previous option
        this.onOptionUpArrow(e);
        break;
      case 'ArrowDown':
        // want to highlight next suggestion
        this.onOptionDownArrow(e);
        break;
      case 'Enter':
        // want to select the suggestion
        this.onOptionEnter(e);
        break;
      case ' ':
        // want to select the suggestion
        this.onOptionSpace(e);
        break;
      case 'Escape':
        // want to hide options
        e.preventDefault(); // prevent bubbling if open in modal
        this.onOptionEscape(e);
        break;
      case 'Tab':
        this.hideListbox();
        this.removeComboboxFocus();
        break;
      default:
        break;
    }
  };

  onOptionUpArrow = (e) => {
    if (this.activeOptionId) {
      const option = this.getPreviousOption();
      if (option) {
        this.highlightOption(option);
      } else {
        this.querySelector('[role="combobox"]').focus();
        this.hideListbox();
      }
    }
    e.preventDefault();
  };

  onOptionDownArrow = (e) => {
    const option = this.getNextOption();

    if (option) {
      this.highlightOption(option);
    }
    e.preventDefault();
  };

  onOptionEnter = (e) => {
    if (this.activeOptionId) {
      this.selectActiveOption();
    }
    // we don't want form to submit
    e.preventDefault();
  };

  onOptionSpace = () => {
    if (this.activeOptionId) {
      this.selectActiveOption();
    }
  };

  onOptionEscape = () => {
    this.hideListbox();
    this.querySelector('[role="combobox"]').focus();
  };

  getActiveOption = () => {
    return this.querySelector(`#${this.activeOptionId}`);
  };

  getPreviousOption = () => {
    return this.getActiveOption().previousSibling;
  };

  getNextOption = () => {
    return this.getActiveOption().nextSibling;
  };

  getFirstOption = () => {
    return this.querySelector('[role="option"]');
  };

  selectOption = (option) => {
    const value =
      option.getAttribute('data-option-value') ||
      option.closest('[role="option"]').getAttribute('data-option-value');

    this.setValue(value);
    this.hideListbox();
    this.querySelector('[role="combobox"]').focus();

    this.emit('select', { value });
  };

  selectActiveOption = () => {
    this.selectOption(this.querySelector(`#${this.activeOptionId}`));
  };

  highlightOption = (option, setFocus = true) => {
    if (this.activeOptionId) {
      const activeOption = this.querySelector(`#${this.activeOptionId}`);
      activeOption.setAttribute('aria-selected', 'false');
    }

    option.setAttribute('aria-selected', 'true');
    this.activeOptionId = option.id;

    if (setFocus) {
      option.focus();
    }
  };

  setValue = (val) => {
    this.select.value = val;
    this.querySelector('[role="combobox"]').innerHTML = this.getComboboxHtml
      ? this.getComboboxHtml(this.select.value)
      : this.getOptionHtml(this.select.value);
    this.querySelector('[role="combobox"]').innerHTML += this.comboboxArrow;
  };

  // Sync values between mobile/desktop instances of component
  handleManualSetOption = (e) => {
    // If value is not set, then set it
    if (this.select.value !== e.detail.value) {
      this.setValue(e.detail.value);
      this.highlightOption(this.querySelector(`[role='option'][data-option-value='${e.detail.value}']`, false));
    }
  };
}
